/**
 * Hostname-aware authenticated landing routes.
 *
 * Domain ownership:
 * - app.fundelitehq.com  → FundElite platform/admin console
 * - app.fundgeniushq.com → FundGenius HQ portal
 *
 * Root `/` on FundElite must never inherit FundGenius or Agencies as the default.
 * `/platform/agencies` is reachable only via explicit nav / URL / same-portal return.
 */

import {
  getHostnamePortal,
  HOSTNAME_PORTALS,
  getDashboardForRoleOnPortal,
  isPlatformConsoleRole,
} from './hostnamePortal';
import {
  getClientPortalDashboardPath,
  hasValidAgencyId,
  isAgencyUser,
  isClientPortalUser,
  isPlatformAdmin,
  isSelfRepairUser,
} from './portalAccess';

export const FUNDELITE_PLATFORM_LANDING = '/platform/dashboard';
export const FUNDELITE_AGENCIES_PATH = '/platform/agencies';
/** Canonical FundGenius HQ dashboard (index under /platform/fundgenius-hq). */
export const FUNDGENIUS_PLATFORM_LANDING = '/platform/fundgenius-hq';
/** Alias accepted by product docs / redirects. */
export const FUNDGENIUS_PLATFORM_DASHBOARD_ALIAS = '/platform/fundgenius-hq/dashboard';
export const FUNDGENIUS_AGENCY_LANDING = '/fundgenius-hq';
export const FUNDGENIUS_AGENCY_CLIENTS_LANDING = '/fundgenius-hq/clients';
export const FUNDGENIUS_PLATFORM_CLIENTS_LANDING = '/platform/fundgenius-hq/clients';

function normalizeHostname(hostname) {
  return String(hostname || '').toLowerCase().split(':')[0];
}

function normalizeRole(user) {
  return String(
    user?.effective_role
    || user?.active_account?.effective_role
    || user?.active_account?.role
    || user?.role
    || ''
  ).toLowerCase();
}

function needsEmailVerification(user) {
  if (!user || !isAgencyUser(user)) return false;
  if (user.email_verified === true) return false;
  if (user.requires_email_verification === false) return false;
  return user.requires_email_verification === true || user.email_verified === false;
}

export function isFundGeniusLandingHost(hostname) {
  const host = normalizeHostname(hostname);
  if (!host) return false;
  if (host === 'app.fundgeniushq.com' || host.endsWith('.fundgeniushq.com')) {
    // Marketing apex (fundgeniushq.com / www) is not the app landing host.
    if (host === 'fundgeniushq.com' || host === 'www.fundgeniushq.com') return false;
    if (host === 'intake.fundgeniushq.com' || host.startsWith('intake.')) return false;
    return true;
  }
  return getHostnamePortal(host) === HOSTNAME_PORTALS.FUNDGENIUS_APP;
}

export function isFundElitePlatformLandingHost(hostname) {
  const host = normalizeHostname(hostname);
  if (!host) return false;
  if (host === 'app.fundelitehq.com' || host.endsWith('.fundelitehq.com')) {
    return true;
  }
  return getHostnamePortal(host) === HOSTNAME_PORTALS.ADMIN;
}

export function isFundGeniusPath(pathname = '') {
  const path = String(pathname || '');
  return (
    path === '/fundgenius-hq'
    || path.startsWith('/fundgenius-hq/')
    || path === '/platform/fundgenius-hq'
    || path.startsWith('/platform/fundgenius-hq/')
  );
}

/** True for the Agencies list (not agency detail deep-links). */
export function isPlatformAgenciesListPath(pathname = '') {
  const path = String(pathname || '').split('?')[0].split('#')[0];
  return path === FUNDELITE_AGENCIES_PATH || path === `${FUNDELITE_AGENCIES_PATH}/`;
}

/**
 * Log landing resolution. Always emits so production root/login can be diagnosed.
 */
export function logPortalLandingRoute({ hostname, role, resolved, reason, portal, accountType }) {
  // Keep the historical tag plus a PORTAL alias used in preview diagnostics.
  const line =
    `hostname=${hostname || ''} role=${role || ''} portal=${portal || ''} `
    + `account_type=${accountType || ''} resolved=${resolved || ''} reason=${reason || ''}`;
  console.info(`[PortalLandingRoute] ${line}`);
  console.info(`[PORTAL] authenticated route origin=${typeof window !== 'undefined' ? window.location.origin : ''} ${line}`);
}

/**
 * Explicit FundElite platform-console default. Never Agencies, never first sidebar item.
 */
export function getFundElitePlatformConsoleDefault(role) {
  const normalized = String(role || '').toLowerCase();
  if (normalized === 'platform_owner') {
    return {
      path: FUNDELITE_PLATFORM_LANDING,
      reason: 'fundelite_platform_owner_default',
    };
  }
  if (normalized === 'platform_admin') {
    return {
      path: FUNDELITE_PLATFORM_LANDING,
      reason: 'fundelite_platform_admin_default',
    };
  }
  if (normalized === 'super_admin') {
    return {
      path: FUNDELITE_PLATFORM_LANDING,
      reason: 'fundelite_super_admin_default',
    };
  }
  if (isPlatformConsoleRole(normalized)) {
    return {
      path: FUNDELITE_PLATFORM_LANDING,
      reason: 'fundelite_platform_console_default',
    };
  }
  return null;
}

/**
 * Shared default-route matrix by host + role.
 * Must never return `/platform/agencies` as an implicit default.
 */
export function getDefaultRouteForRole({
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
  user,
} = {}) {
  return resolvePortalLandingDecision({ hostname, user });
}

/**
 * Role-based fallback when hostname is not a dedicated portal app host
 * (localhost / preview / customer app).
 */
export function resolveRoleBasedFallback(user) {
  return resolvePortalLandingDecision({
    hostname: '',
    user,
    forceRoleFallback: true,
  }).path;
}

function resolvePortalLandingDecision({
  hostname = '',
  user,
  forceRoleFallback = false,
} = {}) {
  const normalizedHost = normalizeHostname(hostname);
  const role = normalizeRole(user);

  if (!user) {
    return { path: '/login', reason: 'unauthenticated', hostname: normalizedHost, role };
  }

  if (
    user.requires_account_selection
    || user.pending_account_selection
    || user.account_selection_required
  ) {
    return {
      path: '/choose-account',
      reason: 'account_selection_required',
      hostname: normalizedHost,
      role,
    };
  }

  // Canonical portal/account_type MUST beat legacy agency_id / role noise.
  const portal = String(
    user?.portal
    || user?.active_account?.portal
    || user?.selected_account?.portal
    || ''
  ).toLowerCase();
  const accountType = String(
    user?.account_type
    || user?.active_account?.type
    || user?.selected_account?.account_type
    || ''
  ).toLowerCase();

  if (portal === 'self_repair' || accountType === 'self_repair') {
    return {
      path: '/dashboard',
      reason: 'self_repair_scoped_default',
      hostname: normalizedHost,
      role: role || 'self_repair',
    };
  }

  if (needsEmailVerification(user)) {
    return {
      path: '/verify-email',
      reason: 'email_verification_required',
      hostname: normalizedHost,
      role,
    };
  }

  if (!forceRoleFallback && isFundGeniusLandingHost(normalizedHost)) {
    if (isPlatformAdmin(user) || isPlatformConsoleRole(role)) {
      return {
        path: FUNDGENIUS_PLATFORM_LANDING,
        reason: 'fundgenius_platform_default',
        hostname: normalizedHost,
        role,
      };
    }
    return {
      path: FUNDGENIUS_AGENCY_LANDING,
      reason: 'fundgenius_agency_default',
      hostname: normalizedHost,
      role,
    };
  }

  if (!forceRoleFallback && isFundElitePlatformLandingHost(normalizedHost)) {
    const platformDefault = getFundElitePlatformConsoleDefault(role);
    if (platformDefault) {
      return {
        path: platformDefault.path,
        reason: platformDefault.reason,
        hostname: normalizedHost,
        role,
      };
    }

    if (isClientPortalUser(user) || isSelfRepairUser(user)) {
      return {
        path: getClientPortalDashboardPath(user),
        reason: 'fundelite_client_portal',
        hostname: normalizedHost,
        role,
      };
    }

    if (isAgencyUser(user) && hasValidAgencyId(user)) {
      return {
        path: `/agency/${user.agency_id || user.active_account?.account_id}/dashboard`,
        reason: 'fundelite_agency_dashboard',
        hostname: normalizedHost,
        role,
      };
    }

    const staffOrOther = getDashboardForRoleOnPortal(user.role, undefined, user);
    return {
      path: rejectAgenciesAsDefault(staffOrOther),
      reason: 'fundelite_role_portal_fallback',
      hostname: normalizedHost,
      role,
    };
  }

  if (isClientPortalUser(user) || isSelfRepairUser(user)) {
    return {
      path: getClientPortalDashboardPath(user),
      reason: 'client_portal_default',
      hostname: normalizedHost,
      role,
    };
  }

  const platformDefault = getFundElitePlatformConsoleDefault(role);
  if (platformDefault) {
    return {
      path: platformDefault.path,
      reason: platformDefault.reason,
      hostname: normalizedHost,
      role,
    };
  }

  if (isAgencyUser(user) && hasValidAgencyId(user)) {
    const agencyId = user.agency_id || user.active_account?.account_id;
    return {
      path: `/agency/${agencyId}/dashboard`,
      reason: 'agency_dashboard_default',
      hostname: normalizedHost,
      role,
    };
  }

  const fallback = getDashboardForRoleOnPortal(user.role, undefined, user);
  return {
    path: rejectAgenciesAsDefault(fallback),
    reason: 'role_portal_fallback',
    hostname: normalizedHost,
    role,
  };
}

/**
 * Agencies list must never win merely because it is an allowed / first sidebar route.
 */
export function rejectAgenciesAsDefault(pathname) {
  if (isPlatformAgenciesListPath(pathname)) {
    return FUNDELITE_PLATFORM_LANDING;
  }
  return pathname || FUNDELITE_PLATFORM_LANDING;
}

/**
 * Resolve the authenticated root / post-login landing route for the current host.
 */
export function getPortalLandingRoute({
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
  user,
  log = true,
} = {}) {
  const decision = resolvePortalLandingDecision({ hostname, user });
  const resolved = rejectAgenciesAsDefault(decision.path);

  if (log) {
    logPortalLandingRoute({
      hostname: decision.hostname || normalizeHostname(hostname),
      role: decision.role || normalizeRole(user),
      portal: user?.portal || user?.active_account?.portal || null,
      accountType: user?.account_type || user?.active_account?.type || null,
      resolved,
      reason: decision.reason,
    });
  }

  return resolved;
}

/**
 * Authoritative root `/` resolver for FundElite / FundGenius app hosts.
 * Callers must navigate with replace: true after auth + role are ready.
 */
export function resolveAuthenticatedRootRoute({
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
  user,
  log = true,
} = {}) {
  const decision = resolvePortalLandingDecision({ hostname, user });
  let resolved = rejectAgenciesAsDefault(decision.path);
  let reason = decision.reason;

  if (isFundElitePlatformLandingHost(hostname) && getFundElitePlatformConsoleDefault(user?.role)) {
    resolved = FUNDELITE_PLATFORM_LANDING;
    reason = getFundElitePlatformConsoleDefault(user?.role).reason;
  }

  if (log) {
    logPortalLandingRoute({
      hostname: normalizeHostname(hostname),
      role: normalizeRole(user),
      resolved,
      reason,
    });
  }

  return resolved;
}

/**
 * Auth callback landing — same matrix as login/root for FundElite platform console.
 * Explicit same-portal next (including /platform/agencies) is preserved.
 */
export function resolveAuthCallbackLanding({
  next,
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
  user,
  log = true,
} = {}) {
  if (
    isFundElitePlatformLandingHost(hostname)
    && getFundElitePlatformConsoleDefault(user?.role)
    && (
      !next
      || next === '/'
      || next === '/login'
      || next === '/signin'
      || isFundGeniusPath(next)
    )
  ) {
    return getPortalLandingRoute({ hostname, user, log });
  }

  if (next && next !== '/' && next !== '/login' && next !== '/signin') {
    return next;
  }

  return getPortalLandingRoute({ hostname, user, log });
}

export default {
  FUNDELITE_PLATFORM_LANDING,
  FUNDELITE_AGENCIES_PATH,
  FUNDGENIUS_PLATFORM_LANDING,
  FUNDGENIUS_PLATFORM_DASHBOARD_ALIAS,
  FUNDGENIUS_AGENCY_LANDING,
  isFundGeniusLandingHost,
  isFundElitePlatformLandingHost,
  isFundGeniusPath,
  isPlatformAgenciesListPath,
  getFundElitePlatformConsoleDefault,
  getDefaultRouteForRole,
  resolveRoleBasedFallback,
  getPortalLandingRoute,
  resolveAuthenticatedRootRoute,
  resolveAuthCallbackLanding,
  rejectAgenciesAsDefault,
  logPortalLandingRoute,
};
